import React, { useCallback } from 'react';

import { useSlider } from '@/hooks/useSlider';

import Button from '@/components/tangerina/Button';
import Card from '@/components/tangerina/Card';

import { TCard } from '@/services/requests/Recommendation/types/template8';
import { TRecommendation } from '@/services/requests/Recommendation/types/template7';
import { formatNumber } from '@/helpers/StringHelper/formatNumber';

export type TProps = {
  template: string;
  cards: TCard[];
};

export default function TableMobile({ template, cards }: TProps) {
  const prefixTemplateClass = `vitrine-template${template}`;

  const { sliderState, moveSliderTo, containerRef } = useSlider(
    { cards } as unknown as TRecommendation,
    '',
    true,
    '8',
    `${prefixTemplateClass}__table__card`,
  );

  const textToObject = useCallback(
    (card: TCard) => {
      return {
        departure: card.card_text[0],
        destination: card.card_text[1],
        date: card.card_text[2],
        miles: card.card_text[3],
      };
    },
    [cards],
  );

  function splitMilesText(text: string): {
    numberPart: string;
    unitPart: string;
  } {
    const [numberPart, ...rest] = text.split(' ');
    return {
      numberPart,
      unitPart: rest.join(' '),
    };
  }

  return (
    <div
      className={`${prefixTemplateClass}__table ${prefixTemplateClass}__table--mobile`}
    >
      <div
        className={`${prefixTemplateClass}__table__cards`}
        ref={containerRef}
      >
        {cards.map((card, index) => {
          if (!card.card_text || card.card_text.length < 4) {
            return null;
          }

          const data = textToObject(card);

          return (
            <div
              className={`${prefixTemplateClass}__table__card`}
              key={`card-${card.id}-${index}-mobile`}
            >
              <Card appearance="outline">
                <div className={`${prefixTemplateClass}__table__card__inner`}>
                  <div
                    className={`${prefixTemplateClass}__table__card__header`}
                  >
                    <div
                      className={`${prefixTemplateClass}__table__card__header__data`}
                    >
                      <tgr-text-heading size="2xs" tag="span">
                        <span
                          className={`${prefixTemplateClass}__table__card__header__data__title`}
                        >
                          Origem
                        </span>
                      </tgr-text-heading>
                      <tgr-text-description>
                        {data.departure.text?.replace('Saindo de ', '')}
                      </tgr-text-description>
                    </div>
                    <tgr-icon icon={'airplanemode-active' as any} />
                    <div
                      className={`${prefixTemplateClass}__table__card__header__data`}
                    >
                      <tgr-text-heading size="2xs" tag="span">
                        <span
                          className={`${prefixTemplateClass}__table__card__header__data__title`}
                        >
                          Destino
                        </span>
                      </tgr-text-heading>
                      <tgr-text-description>
                        {data.destination.text}
                      </tgr-text-description>
                    </div>
                  </div>
                  <tgr-divider />
                  <div className={`${prefixTemplateClass}__table__card__body`}>
                    {/* Data */}
                    <div
                      className={`${prefixTemplateClass}__table__card__body__data ${prefixTemplateClass}__table__card__body__data--date`}
                    >
                      <tgr-text-heading size="2xs" tag="span">
                        <span
                          className={`${prefixTemplateClass}__table__card__body__data__title`}
                        >
                          Data do voo <span>- Só ida ou volta</span>
                        </span>
                      </tgr-text-heading>
                      <tgr-text-description>
                        <span
                          className={`${prefixTemplateClass}__table__card__body__data__data ${prefixTemplateClass}__table__card__body__data__data--date`}
                        >
                          {data.date.image?.url && (
                            <img src={data.date.image.url} alt="calendar icon" />
                          )}
                          {data.date.text}
                        </span>
                      </tgr-text-description>
                    </div>

                    {/* Preço */}
                    <div
                      className={`${prefixTemplateClass}__table__card__body__data ${prefixTemplateClass}__table__card__body__data--price`}
                    >
                      <tgr-text-heading size="2xs" tag="span">
                        <span
                          className={`${prefixTemplateClass}__table__card__body__data__title`}
                        >
                          Trechos a partir de
                        </span>
                      </tgr-text-heading>
                      <div
                        className={`${prefixTemplateClass}__table__card__body__data__flex`}
                      >
                        <tgr-text-heading size="sm">
                          <span
                            className={`${prefixTemplateClass}__table__card__body__data__data ${prefixTemplateClass}__table__card__body__data__data--price`}
                          >
                            {data.miles.text &&
                              (() => {
                                const { numberPart, unitPart } = splitMilesText(
                                  data.miles.text,
                                );
                                return (
                                  <>
                                    <span className="miles-number">
                                      {formatNumber(numberPart)}
                                    </span>{' '}
                                    <span className="miles-unit">{unitPart}</span>
                                  </>
                                );
                              })()}
                          </span>
                        </tgr-text-heading>
                        {data.miles.tag_text && (
                          <span
                            className={`${prefixTemplateClass}__table__tag ${prefixTemplateClass}__table__tag--${
                              data.miles.tag_color ?? 'default'
                            }`}
                          >
                            {data.miles.tag_text}
                          </span>
                        )}
                      </div>
                      <div
                        className={`${prefixTemplateClass}__table__card__body__data__descriptions`}
                      >
                        <tgr-text-description>
                          {data.miles.subtexto}
                        </tgr-text-description>
                        <tgr-text-description>
                          *Taxas de embarque não inclusas
                        </tgr-text-description>
                      </div>
                    </div>

                    {/* CTA */}
                    <div
                      className={`${prefixTemplateClass}__table__card__body__data ${prefixTemplateClass}__table__card__body__data--cta`}
                    >
                      <Button appearance="secondary">
                        <span
                          className={`${prefixTemplateClass}__table__card__body__data__button`}
                        >
                          {card.card_link?.label}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          );
        })}
      </div>

      <div className={`${prefixTemplateClass}__table__dots`}>
        {cards.map((card, i) => {
          return (
            <span
              role="button"
              key={`card-${card.id}-${i}-mobile-dot`}
              className={`${prefixTemplateClass}__table__dot${
                i + 1 === sliderState.current
                  ? ` ${prefixTemplateClass}__table__dot--current`
                  : ''
              }`}
              aria-hidden="true"
              tabIndex={0}
              onClick={() => moveSliderTo(i)}
            />
          );
        })}
      </div>
    </div>
  );
}
